import { Stack as ExpoStack, router, useLocalSearchParams } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import { useEffect, useState, type ReactNode } from 'react';
import { StyleSheet, Text as RNText, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Avatar } from '@/components/Avatar';
import { BackChevron } from '@/components/BackChevron';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/Button';
import {
  ACTIVITY_KIND_LABEL,
  ACTIVITY_KIND_PILL_TONE,
  ACTIVITY_STATUS_LABEL,
  ACTIVITY_STATUS_PILL_TONE,
} from '@/components/ActivityRow';
import { EmptyState } from '@/components/EmptyState';
import { Heading } from '@/components/Heading';
import { Inline } from '@/components/Inline';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Overline } from '@/components/Overline';
import { Pill } from '@/components/Pill';
import { Screen } from '@/components/Screen';
import { Skeleton } from '@/components/Skeleton';
import { Stack } from '@/components/Stack';
import { Text } from '@/components/Text';
import { colors, fonts, spacing } from '@/constants/theme';
import { apiClient } from '@/services/api';
import { earningsStore } from '@/stores/earnings.store';
import { EARNING_KIND, type Earning } from '@/types/earning';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Detail screen owns local fetch state for the deep-link case (id not
// yet in the paginated list). 95% of arrivals are taps from a Recent
// Activity row or the /earnings list, where `fromList` resolves
// immediately and no fetch fires. The other 5% (deep-link, paginated
// past, refresh in progress) fall through to an inline GET. No store
// cache, no service method — single-consumer single-GET stays
// right-sized for v1.
export default function EarningDetailScreen() {
  const params = useLocalSearchParams<{ id: string }>();
  const numId = params.id ? parseInt(params.id, 10) : NaN;

  const fromList = !Number.isNaN(numId)
    ? earningsStore.earnings.find((e) => e.id === numId)
    : undefined;

  const [{ earning, loading, error }, setState] = useState<{
    earning: Earning | null;
    loading: boolean;
    error: unknown;
  }>({
    earning: fromList ?? null,
    loading: !Number.isNaN(numId) && !fromList,
    error: null,
  });

  useEffect(() => {
    if (Number.isNaN(numId) || fromList) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get<{ earning: Earning }>(`/api/v1/earnings/${numId}`)
      .then((response) => {
        if (!cancelled) {
          setState({ earning: response.earning, loading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ earning: null, loading: false, error: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [numId, fromList]);

  if (Number.isNaN(numId) || error) {
    return <NotFound />;
  }

  if (loading) {
    return <DetailSkeleton />;
  }

  if (!earning) {
    return <NotFound />;
  }

  return <FoundView earning={earning} />;
}

function NotFound() {
  return (
    <>
      <ExpoStack.Screen options={{ headerShown: false }} />
      <Screen>
        <Stack gap="lg">
          <BackChevron />
          <EmptyState
            title="Earning not found"
            body="That earning isn't on your account anymore."
          />
        </Stack>
      </Screen>
    </>
  );
}

function DetailSkeleton() {
  const insets = useSafeAreaInsets();
  return (
    <>
      <ExpoStack.Screen options={{ headerShown: false }} />
      <Screen scroll edges={[]} padding="none">
        <View style={[styles.hero, { paddingTop: insets.top + spacing.lg }]}>
          <RNText style={styles.watermark} pointerEvents="none">
            MP
          </RNText>
          <View style={[styles.backAbsolute, { top: insets.top + spacing.sm }]}>
            <BackChevron tone="onDark" />
          </View>
          <Stack gap="lg" align="center" style={styles.heroContent}>
            <Skeleton width={56} height={56} radius="base" />
            <Skeleton width={180} height={24} />
            <Skeleton width={120} height={14} />
            <Skeleton width={160} height={56} />
          </Stack>
        </View>
      </Screen>
    </>
  );
}

function FoundView({ earning }: { earning: Earning }) {
  const insets = useSafeAreaInsets();
  const initials = earning.deal.brand_name.slice(0, 2).toUpperCase();
  const statusTone = ACTIVITY_STATUS_PILL_TONE[earning.status];
  const statusLabel = ACTIVITY_STATUS_LABEL[earning.status];
  const kindTone = ACTIVITY_KIND_PILL_TONE[earning.kind];
  const kindLabel = ACTIVITY_KIND_LABEL[earning.kind];

  return (
    <>
      <ExpoStack.Screen options={{ headerShown: false }} />
      <Screen scroll edges={[]} padding="none">
        <View style={[styles.hero, { paddingTop: insets.top + spacing.lg }]}>
          <RNText style={styles.watermark} pointerEvents="none">
            MP
          </RNText>
          <View style={[styles.backAbsolute, { top: insets.top + spacing.sm }]}>
            <BackChevron tone="onDark" />
          </View>
          <Stack gap="lg" align="center" style={styles.heroContent}>
            <BrandLogo
              url={earning.deal.brand_logo_url}
              size={56}
              fallback={<Avatar initials={initials} size={56} tone="electric" />}
            />
            <Heading size="2xl" tone="inverse" style={styles.brandName}>
              {earning.deal.display_name}
            </Heading>
            <Overline tone="onDark">{earning.deal.brand_category}</Overline>
            <MoneyDisplay
              cents={earning.amount_cents}
              size="hero"
              tone="onDark"
              showCents
            />
          </Stack>
        </View>

        <Stack gap="md" style={styles.metaPanel}>
          <MetaRow
            label="Date"
            value={<Text>{formatDateTime(earning.created_at)}</Text>}
          />
          <MetaRow label="Type" value={<Pill tone={kindTone}>{kindLabel}</Pill>} />
          {earning.kind === EARNING_KIND.Referral && earning.person ? (
            <MetaRow label="Person" value={<Text>{earning.person.public_name}</Text>} />
          ) : null}
          <MetaRow
            label="Status"
            value={<Pill tone={statusTone}>{statusLabel}</Pill>}
          />
          <Button
            label={`View ${earning.deal.display_name} deal`}
            variant="outline"
            size="base"
            fullWidth
            trailingIcon={ArrowRight}
            onPress={() => router.push(`/deals/${earning.deal.slug}`)}
            testID="earning-view-deal"
          />
        </Stack>
      </Screen>
    </>
  );
}

function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Inline justify="space-between" align="center">
      <Text variant="caption" color="textMuted">
        {label}
      </Text>
      <View>{value}</View>
    </Inline>
  );
}

const styles = StyleSheet.create({
  hero: {
    backgroundColor: colors.deepGreen,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
    overflow: 'hidden',
  },
  heroContent: {
    marginTop: spacing.xl,
  },
  brandName: {
    textAlign: 'center',
  },
  // Same watermark metrics as `app/(tabs)/index.tsx` and DealHeroCard.
  watermark: {
    position: 'absolute',
    top: -32,
    right: -36,
    fontFamily: fonts.numeric,
    fontSize: 200,
    lineHeight: 200,
    color: 'rgba(248, 252, 249, 0.08)',
    transform: [{ rotate: '-8deg' }],
    letterSpacing: -10,
  },
  backAbsolute: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 10,
  },
  metaPanel: {
    padding: spacing.lg,
  },
});
